import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { useCurrentOrg } from '../orgs/useCurrentOrg';
import { getRun, listRunEvents, type AgentRun } from './api';
import { RunTraceList } from './RunTraceList';

const POLL_INTERVAL = 3000;

export function RunDetailPage() {
  const { org } = useCurrentOrg();
  const orgId = org?.id ?? '';
  const { agentID, runID } = useParams<{ agentID: string; runID: string }>();

  const run = useQuery({
    queryKey: ['run', orgId, agentID, runID],
    queryFn: () => getRun(orgId, agentID!, runID!),
    enabled: !!orgId && !!agentID && !!runID,
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return status === 'done' || status === 'failed' ? false : POLL_INTERVAL;
    },
  });

  const events = useQuery({
    queryKey: ['run-events', orgId, agentID, runID],
    queryFn: () => listRunEvents(orgId, agentID!, runID!, 0),
    enabled: !!orgId && !!agentID && !!runID,
    refetchInterval: (q) => {
      const status = run.data?.status;
      if (status === 'done' || status === 'failed') {
        // One last refetch after completion, then stop.
        return q.state.dataUpdateCount === 0 ? POLL_INTERVAL : false;
      }
      return POLL_INTERVAL;
    },
  });

  if (!run.data) return <p className="text-sm text-ink-tertiary">Loading…</p>;
  const isLive = run.data.status === 'pending' || run.data.status === 'running';

  return (
    <div className="space-y-5">
      <RunHeader agentID={agentID!} run={run.data} />

      {run.data.error_message && (
        <Card>
          <CardHeader title="Error" />
          <CardBody>
            <pre className="whitespace-pre-wrap text-sm text-danger">
              {run.data.error_message}
            </pre>
          </CardBody>
        </Card>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <RunTraceList
            events={events.data ?? []}
            loading={events.isLoading}
            liveStreaming={isLive}
          />
        </div>
        <div className="space-y-5">
          <Card>
            <CardHeader title="Objective" />
            <CardBody>
              <p className="whitespace-pre-wrap text-sm text-ink-primary">{run.data.objective}</p>
            </CardBody>
          </Card>
          {run.data.summary && (
            <Card>
              <CardHeader
                title="Final summary"
                description="The agent's own description of what it did."
              />
              <CardBody>
                <p className="whitespace-pre-wrap text-sm text-ink-primary">{run.data.summary}</p>
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function RunHeader({ agentID, run }: { agentID: string; run: AgentRun }) {
  const duration = computeDuration(run);
  return (
    <div className="sticky top-0 z-20 -mx-6 -mt-6 mb-2 border-b border-line-soft bg-surface-white/95 px-6 pb-4 pt-6 backdrop-blur">
      <Link to={`/agents/${agentID}`} className="font-mono text-[11px] text-bit2me hover:underline">
        ← Back to agent
      </Link>
      <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-xl font-semibold tracking-tightish text-ink-primary">
            Run #{run.id.slice(0, 8)}
          </h1>
          <p className="mt-1 truncate text-xs text-ink-secondary">
            Triggered by{' '}
            <code className="font-mono text-[11px] text-ink-primary">{run.triggered_by}</code>{' '}
            on {new Date(run.created_at).toLocaleString()}
          </p>
        </div>
        <RunStatusPill run={run} />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Duration" value={duration} />
        <Stat label="Tokens in" value={String(run.tokens_input)} mono />
        <Stat label="Tokens out" value={String(run.tokens_output)} mono />
        <Stat label="Outcome" value={run.outcome ?? '—'} tone={outcomeTone(run.outcome)} />
      </div>
    </div>
  );
}

function RunStatusPill({ run }: { run: AgentRun }) {
  const tone =
    run.status === 'done'
      ? 'border-ok/30 bg-ok/10 text-ok'
      : run.status === 'failed'
      ? 'border-danger/30 bg-danger/10 text-danger'
      : run.status === 'running'
      ? 'border-amber-200 bg-amber-50 text-amber-700'
      : 'border-line bg-surface-soft text-ink-tertiary';
  const live = run.status === 'pending' || run.status === 'running';
  return (
    <span
      className={`flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase ${tone}`}
    >
      {live && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />}
      {run.status}
    </span>
  );
}

function Stat({
  label,
  value,
  mono = false,
  tone = '',
}: {
  label: string;
  value: string;
  mono?: boolean;
  tone?: string;
}) {
  return (
    <div className="rounded-lg border border-line-soft bg-surface-soft/30 px-3 py-1.5">
      <p className="font-mono text-[10px] uppercase tracking-eyebrow text-ink-tertiary">{label}</p>
      <p className={`mt-0.5 text-sm font-semibold ${mono ? 'font-mono' : ''} ${tone || 'text-ink-primary'}`}>
        {value}
      </p>
    </div>
  );
}

function computeDuration(run: AgentRun): string {
  const start = run.started_at ?? run.created_at;
  const end = run.finished_at ?? new Date().toISOString();
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) return rs ? `${m}m ${rs}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `${h}h ${rm}m` : `${h}h`;
}

function outcomeTone(outcome?: string): string {
  if (!outcome) return '';
  if (outcome === 'completed') return 'text-ok';
  if (outcome === 'failed' || outcome === 'timeout') return 'text-danger';
  if (outcome === 'quota_exceeded' || outcome === 'policy_rejected') return 'text-amber-600';
  return '';
}
